import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { API, authFetch } from "@/App";
import {
  Handshake, Play, Loader2, Send, Check, X, Eye,
  Building2, Mail, Clock, Target, ExternalLink, Trash2,
  MessageSquare, RefreshCw, Shield
} from "lucide-react";
import DOMPurify from "dompurify";

const STATUS_COLORS = {
  researched: "bg-blue-100 text-blue-700",
  queued: "bg-amber-100 text-amber-700",
  approved: "bg-purple-100 text-purple-700",
  sent: "bg-emerald-100 text-emerald-700",
  replied: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
};

const ORG_COLORS = {
  cdfi: "bg-blue-50 text-blue-700 border-blue-200",
  sba_lender: "bg-purple-50 text-purple-700 border-purple-200",
  grant_program: "bg-amber-50 text-amber-700 border-amber-200",
  economic_dev: "bg-emerald-50 text-emerald-700 border-emerald-200",
  local_bank: "bg-rose-50 text-rose-700 border-rose-200",
};

export function PartnershipAgentDashboard({ open, onOpenChange }) {
  const [status, setStatus] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [queue, setQueue] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [activeTab, setActiveTab] = useState("queue");
  const [previewDraft, setPreviewDraft] = useState(null);
  const [sending, setSending] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, prospectsRes, queueRes, logsRes] = await Promise.all([
        authFetch(`${API}/partnership-agent/status`),
        authFetch(`${API}/partnership-agent/prospects`),
        authFetch(`${API}/partnership-agent/review-queue`),
        authFetch(`${API}/partnership-agent/logs`),
      ]);
      if (statusRes.ok) setStatus(await statusRes.json());
      if (prospectsRes.ok) setProspects((await prospectsRes.json()).prospects || []);
      if (queueRes.ok) setQueue((await queueRes.json()).queue || []);
      if (logsRes.ok) setLogs((await logsRes.json()).logs || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { if (open) fetchData(); }, [open, fetchData]);

  const triggerAgent = async () => {
    setTriggering(true);
    try {
      const res = await authFetch(`${API}/partnership-agent/trigger`, { method: "POST" });
      if (res.ok) { const d = await res.json(); toast.success(`Found ${d.prospects_added} prospects — queued for review`); fetchData(); }
      else toast.error("Agent run failed");
    } catch { toast.error("Failed"); }
    setTriggering(false);
  };

  const toggleAgent = async () => {
    const res = await authFetch(`${API}/partnership-agent/toggle`, { method: "POST" });
    if (res.ok) { const d = await res.json(); toast.success(d.enabled ? "Agent enabled" : "Agent disabled"); fetchData(); }
  };

  const approveAndSend = async (prospectId) => {
    setSending(prospectId);
    try {
      const res = await authFetch(`${API}/partnership-agent/approve/${prospectId}`, { method: "POST" });
      if (res.ok) { toast.success("Email sent!"); fetchData(); }
      else { const e = await res.json().catch(() => ({})); toast.error(e.detail || "Failed"); }
    } catch { toast.error("Failed to send"); }
    setSending(null);
  };

  const rejectProspect = async (prospectId) => {
    const reason = prompt("Reason for rejection (optional):");
    const res = await authFetch(`${API}/partnership-agent/reject/${prospectId}`, { method: "POST", body: JSON.stringify({ reason: reason || "" }) });
    if (res.ok) { toast.success("Rejected"); fetchData(); }
  };

  const markReplied = async (prospectId) => {
    const res = await authFetch(`${API}/partnership-agent/mark-replied/${prospectId}`, { method: "POST" });
    if (res.ok) { toast.success("Marked as replied"); fetchData(); }
  };

  const deleteProspect = async (prospectId) => {
    if (!confirm("Delete this prospect?")) return;
    const res = await authFetch(`${API}/partnership-agent/prospects/${prospectId}`, { method: "DELETE" });
    if (res.ok) { toast.success("Deleted"); fetchData(); }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-6xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0" data-testid="partnership-agent-dashboard">
        <DialogHeader className="p-5 border-b">
          <DialogTitle className="flex items-center gap-2">
            <Handshake className="w-5 h-5 text-[#2BC40E]" /> Partnership Outreach Agent
          </DialogTitle>
          <DialogDescription>AI-powered research, outreach drafting & review queue for CDFIs, SBA lenders, grant programs & more</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#39FF14]" /></div>
          ) : (
            <>
              {/* Controls */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-neutral-600">Agent</span>
                    <Switch checked={status?.enabled !== false} onCheckedChange={toggleAgent} />
                  </div>
                  <Button onClick={triggerAgent} disabled={triggering || status?.enabled === false} className="bg-[#333333] text-white" data-testid="trigger-partnership-agent">
                    {triggering ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
                    Research New Partners
                  </Button>
                </div>
                <Button variant="outline" size="sm" onClick={fetchData}><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
              </div>

              {/* Stats */}
              {status?.stats && (
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6">
                  {[
                    { label: "Total", value: status.stats.total_prospects, color: "text-neutral-700" },
                    { label: "Researched", value: status.stats.researched, color: "text-blue-600" },
                    { label: "In Queue", value: status.stats.in_review_queue, color: "text-amber-600" },
                    { label: "Approved", value: status.stats.approved, color: "text-purple-600" },
                    { label: "Sent", value: status.stats.emails_sent, color: "text-emerald-600" },
                    { label: "Replied", value: status.stats.replies, color: "text-green-600" },
                  ].map(s => (
                    <Card key={s.label} className="border-neutral-200">
                      <CardContent className="p-3 text-center">
                        <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                        <p className="text-[10px] text-neutral-500 uppercase">{s.label}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid grid-cols-4 mb-4">
                  <TabsTrigger value="queue">Review Queue {queue.length > 0 && <Badge className="ml-1 bg-amber-500 text-white text-xs">{queue.length}</Badge>}</TabsTrigger>
                  <TabsTrigger value="sent">Sent & Replies</TabsTrigger>
                  <TabsTrigger value="all">All Prospects</TabsTrigger>
                  <TabsTrigger value="logs">Activity</TabsTrigger>
                </TabsList>

                {/* REVIEW QUEUE */}
                <TabsContent value="queue">
                  {queue.length === 0 ? (
                    <div className="text-center py-12 text-neutral-500">
                      <Shield className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>Review queue is empty</p>
                      <p className="text-sm">Click "Research New Partners" to find prospects</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {queue.map(p => (
                        <Card key={p.prospect_id} className="border-neutral-200" data-testid={`queue-${p.prospect_id}`}>
                          <CardContent className="p-5">
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <h3 className="font-semibold text-neutral-900">{p.org_name}</h3>
                                  <Badge className={ORG_COLORS[p.org_type] || "bg-neutral-100 text-neutral-700"}>{p.org_type_label || p.org_type}</Badge>
                                  <Badge variant="outline" className="text-xs">Score: {p.priority_score}/10</Badge>
                                </div>
                                <p className="text-sm text-neutral-600">{p.description}</p>
                                <div className="flex items-center gap-4 mt-2 text-xs text-neutral-500">
                                  <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{p.location}</span>
                                  <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{p.contact_name} — {p.contact_title}</span>
                                  {p.website && <a href={p.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-500 hover:underline"><ExternalLink className="w-3 h-3" />Website</a>}
                                </div>
                                <p className="text-xs text-neutral-400 mt-1">Source: {p.contact_source}</p>
                              </div>
                            </div>

                            {/* Email Preview */}
                            {p.email_draft && (
                              <div className="bg-neutral-50 rounded-lg p-4 mb-3 border border-neutral-200">
                                <p className="text-xs text-neutral-500 mb-1">Draft Email:</p>
                                <p className="font-medium text-sm text-neutral-900 mb-1">Subject: {p.email_draft.subject}</p>
                                <p className="text-xs text-neutral-500 mb-1">To: {p.contact_email}</p>
                                <div className="text-sm text-neutral-700 mt-2 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(p.email_draft.body) }} />
                              </div>
                            )}

                            {/* Actions */}
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => approveAndSend(p.prospect_id)} disabled={sending === p.prospect_id}
                                className="bg-[#39FF14] text-black hover:bg-[#32E010]" data-testid={`approve-${p.prospect_id}`}>
                                {sending === p.prospect_id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                                Approve & Send
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => rejectProspect(p.prospect_id)}>
                                <X className="w-3.5 h-3.5 mr-1" /> Reject
                              </Button>
                              <Button size="sm" variant="ghost" className="text-red-400" onClick={() => deleteProspect(p.prospect_id)}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* SENT & REPLIES */}
                <TabsContent value="sent">
                  {prospects.filter(p => ["sent", "replied"].includes(p.status)).length === 0 ? (
                    <p className="text-neutral-500 text-center py-12">No sent emails yet</p>
                  ) : (
                    <div className="space-y-3">
                      {prospects.filter(p => ["sent", "replied"].includes(p.status)).map(p => (
                        <Card key={p.prospect_id} className="border-neutral-200">
                          <CardContent className="p-4 flex items-center gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-sm text-neutral-900">{p.org_name}</h3>
                                <Badge className={STATUS_COLORS[p.status]}>{p.status}</Badge>
                                <Badge className={ORG_COLORS[p.org_type] || ""} variant="outline">{p.org_type}</Badge>
                              </div>
                              <p className="text-xs text-neutral-500">{p.contact_name} • {p.contact_email}</p>
                              <p className="text-xs text-neutral-400">Sent: {p.sent_at?.slice(0, 10)}</p>
                            </div>
                            {p.status === "sent" && (
                              <Button size="sm" variant="outline" className="text-green-600 border-green-200" onClick={() => markReplied(p.prospect_id)}>
                                <MessageSquare className="w-3.5 h-3.5 mr-1" /> Mark Replied
                              </Button>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* ALL PROSPECTS */}
                <TabsContent value="all">
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {prospects.map(p => (
                      <div key={p.prospect_id} className="flex items-center gap-3 p-3 bg-neutral-50 rounded-lg">
                        <Badge className={STATUS_COLORS[p.status] || "bg-neutral-100"}>{p.status}</Badge>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-neutral-900 truncate">{p.org_name}</p>
                          <p className="text-xs text-neutral-500">{p.org_type_label} • {p.location}</p>
                        </div>
                        <p className="text-xs text-neutral-400">{p.created_at?.slice(0, 10)}</p>
                        <Button size="sm" variant="ghost" className="text-red-400" onClick={() => deleteProspect(p.prospect_id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    ))}
                    {prospects.length === 0 && <p className="text-neutral-500 text-center py-8">No prospects yet</p>}
                  </div>
                </TabsContent>

                {/* ACTIVITY LOG */}
                <TabsContent value="logs">
                  <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
                    {logs.map(l => (
                      <div key={l.log_id} className="flex items-center gap-3 p-2.5 bg-neutral-50 rounded-lg text-sm">
                        <Clock className="w-3.5 h-3.5 text-neutral-400 flex-shrink-0" />
                        <span className="flex-1 text-neutral-700">{l.message}</span>
                        <span className="text-xs text-neutral-400">{l.created_at?.slice(0, 16).replace("T", " ")}</span>
                      </div>
                    ))}
                    {logs.length === 0 && <p className="text-neutral-500 text-center py-8">No activity yet</p>}
                  </div>
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default PartnershipAgentDashboard;
